import { useState } from 'react'
import { CheckCircle, Copy, Download, Plus, Trash2, ChevronRight, ChevronLeft } from 'lucide-react'
import {
  DEFAULT_DATA, generateStatement, generateHtmlStatement,
  type StatementData, type KnownIssue, type ConformanceStatus
} from './statementUtils'

const TOTAL_STEPS = 5

const TECH_OPTIONS = ['HTML', 'CSS', 'JavaScript', 'WAI-ARIA', 'WordPress', 'React', 'Next.js', 'Drupal', 'Shopify']
const APPROACH_OPTIONS = [
  { value: 'self-evaluation', label: 'Self-evaluation' },
  { value: 'external-audit', label: 'External audit by a third party' },
  { value: 'automated-tools', label: 'Automated evaluation tools' },
  { value: 'user-testing', label: 'User testing with assistive technology users' },
]

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
            i + 1 < current ? 'bg-green-500 text-white' :
            i + 1 === current ? 'bg-purple-600 text-white' :
            'bg-gray-200 text-gray-500'
          }`}>
            {i + 1 < current ? <CheckCircle className="w-4 h-4" /> : i + 1}
          </div>
          {i < total - 1 && (
            <div className={`h-0.5 w-8 transition-colors ${i + 1 < current ? 'bg-green-400' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
      <span className="ml-2 text-sm text-gray-500">Step {current} of {total}</span>
    </div>
  )
}

function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {children}{required && <span className="text-red-500 ml-1">*</span>}
    </label>
  )
}

function Input({ ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${props.className ?? ''}`}
    />
  )
}

export default function AccessibilityStatementWidget() {
  const [step, setStep] = useState(1)
  const [data, setData] = useState<StatementData>(DEFAULT_DATA)
  const [copied, setCopied] = useState<'text' | 'html' | null>(null)
  const [showHtml, setShowHtml] = useState(false)
  const [newIssue, setNewIssue] = useState<KnownIssue>({ description: '', impact: '', workaround: '' })

  const set = (field: keyof StatementData, value: unknown) =>
    setData(d => ({ ...d, [field]: value }))

  const toggleTech = (tech: string) => {
    set('technologies', data.technologies.includes(tech)
      ? data.technologies.filter(t => t !== tech)
      : [...data.technologies, tech]
    )
  }

  const toggleApproach = (val: string) => {
    set('assessmentApproach', data.assessmentApproach.includes(val)
      ? data.assessmentApproach.filter(a => a !== val)
      : [...data.assessmentApproach, val]
    )
  }

  const addIssue = () => {
    if (!newIssue.description.trim()) return
    set('knownIssues', [...data.knownIssues, { ...newIssue }])
    setNewIssue({ description: '', impact: '', workaround: '' })
  }

  const removeIssue = (i: number) =>
    set('knownIssues', data.knownIssues.filter((_, idx) => idx !== i))

  const copyText = (type: 'text' | 'html') => {
    const content = type === 'html' ? generateHtmlStatement(data) : generateStatement(data)
    navigator.clipboard.writeText(content).then(() => {
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    })
  }

  const downloadFile = (type: 'text' | 'html') => {
    const content = type === 'html' ? generateHtmlStatement(data) : generateStatement(data)
    const ext = type === 'html' ? 'html' : 'txt'
    const mime = type === 'html' ? 'text/html' : 'text/plain'
    const blob = new Blob([content], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `accessibility-statement.${ext}`
    a.click()
    URL.revokeObjectURL(url)
  }

  const canProceed = () => {
    if (step === 1) return data.orgName.trim() !== ''
    if (step === 4) return data.contactEmail.trim() !== ''
    return true
  }

  const generatedText = generateStatement(data)
  const generatedHtml = generateHtmlStatement(data)

  return (
    <div id="statement-generator" className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 md:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Accessibility Statement Generator</h2>
      <p className="text-gray-600 mb-6">Answer a few questions to generate a WCAG-compliant accessibility statement for your website.</p>

      {step <= TOTAL_STEPS && <StepIndicator current={step} total={TOTAL_STEPS} />}

      {/* Step 1: Organization */}
      {step === 1 && (
        <div className="space-y-5">
          <h3 className="text-lg font-semibold text-gray-900">Organization & Website</h3>
          <div>
            <Label required>Organization name</Label>
            <Input
              value={data.orgName}
              onChange={e => set('orgName', e.target.value)}
              placeholder="Acme Corp"
              autoFocus
            />
          </div>
          <div>
            <Label>Website name</Label>
            <Input
              value={data.websiteName}
              onChange={e => set('websiteName', e.target.value)}
              placeholder="Acme Online Shop"
            />
          </div>
          <div>
            <Label>Website URL</Label>
            <Input
              type="url"
              value={data.websiteUrl}
              onChange={e => set('websiteUrl', e.target.value)}
              placeholder="https://www.example.com"
            />
          </div>
        </div>
      )}

      {/* Step 2: Conformance */}
      {step === 2 && (
        <div className="space-y-5">
          <h3 className="text-lg font-semibold text-gray-900">Conformance Status</h3>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>WCAG Version</Label>
              <select
                value={data.wcagVersion}
                onChange={e => set('wcagVersion', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value="2.2">WCAG 2.2 (recommended)</option>
                <option value="2.1">WCAG 2.1</option>
              </select>
            </div>
            <div>
              <Label>Target level</Label>
              <select
                value={data.wcagLevel}
                onChange={e => set('wcagLevel', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value="AA">Level AA (required by most laws)</option>
                <option value="AAA">Level AAA (enhanced)</option>
              </select>
            </div>
          </div>

          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-3">Conformance status</legend>
            <div className="space-y-3">
              {([
                { value: 'fully-conformant', label: 'Fully Conformant', desc: 'No known accessibility failures against the target standard.' },
                { value: 'partially-conformant', label: 'Partially Conformant', desc: 'Some parts of the content do not fully conform. Recommended for most sites.' },
                { value: 'non-conformant', label: 'Non-Conformant', desc: 'The content does not conform to the standard.' },
              ] as { value: ConformanceStatus; label: string; desc: string }[]).map(opt => (
                <label key={opt.value} className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  data.conformanceStatus === opt.value
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}>
                  <input
                    type="radio"
                    name="conformance"
                    value={opt.value}
                    checked={data.conformanceStatus === opt.value}
                    onChange={() => set('conformanceStatus', opt.value)}
                    className="mt-0.5 text-purple-600"
                  />
                  <div>
                    <div className="font-medium text-gray-900">{opt.label}</div>
                    <div className="text-sm text-gray-500">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <Label>Date of last assessment</Label>
            <Input
              type="date"
              value={data.assessmentDate}
              onChange={e => set('assessmentDate', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Step 3: Known Issues */}
      {step === 3 && (
        <div className="space-y-5">
          <h3 className="text-lg font-semibold text-gray-900">Known Limitations</h3>
          <p className="text-sm text-gray-600">
            {data.conformanceStatus === 'fully-conformant'
              ? 'You selected "Fully Conformant" — skip this step if there are no known issues.'
              : 'List any known accessibility issues and their workarounds. This improves trust and meets legal requirements in many jurisdictions.'
            }
          </p>

          {/* Existing issues */}
          {data.knownIssues.length > 0 && (
            <div className="space-y-3">
              {data.knownIssues.map((issue, i) => (
                <div key={i} className="flex gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-gray-900">{issue.description}</p>
                    {issue.impact && <p className="text-gray-500 mt-0.5">Impact: {issue.impact}</p>}
                    {issue.workaround && <p className="text-gray-500">Workaround: {issue.workaround}</p>}
                  </div>
                  <button
                    onClick={() => removeIssue(i)}
                    className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    aria-label={`Remove issue ${i + 1}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new issue */}
          <div className="border border-dashed border-gray-300 rounded-lg p-5 space-y-3">
            <p className="text-sm font-medium text-gray-700">Add a known issue</p>
            <div>
              <Label>Description</Label>
              <Input
                value={newIssue.description}
                onChange={e => setNewIssue(n => ({ ...n, description: e.target.value }))}
                placeholder="e.g. Some PDF documents are not fully accessible"
              />
            </div>
            <div>
              <Label>Impact on users <span className="font-normal text-gray-400">(optional)</span></Label>
              <Input
                value={newIssue.impact}
                onChange={e => setNewIssue(n => ({ ...n, impact: e.target.value }))}
                placeholder="e.g. Screen reader users may not access all information"
              />
            </div>
            <div>
              <Label>Workaround <span className="font-normal text-gray-400">(optional)</span></Label>
              <Input
                value={newIssue.workaround}
                onChange={e => setNewIssue(n => ({ ...n, workaround: e.target.value }))}
                placeholder="e.g. Contact us to request an accessible version"
              />
            </div>
            <button
              onClick={addIssue}
              disabled={!newIssue.description.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 transition-colors disabled:opacity-40"
            >
              <Plus className="w-4 h-4" />
              Add Issue
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Contact */}
      {step === 4 && (
        <div className="space-y-5">
          <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
          <p className="text-sm text-gray-600">
            WCAG requires you to provide a way for users to report accessibility problems and request accessible alternatives.
          </p>
          <div>
            <Label>Contact name or role</Label>
            <Input
              value={data.contactName}
              onChange={e => set('contactName', e.target.value)}
              placeholder="e.g. Accessibility Team or Jane Smith, Web Manager"
            />
          </div>
          <div>
            <Label required>Contact email</Label>
            <Input
              type="email"
              value={data.contactEmail}
              onChange={e => set('contactEmail', e.target.value)}
              placeholder="accessibility@example.com"
            />
          </div>
          <div>
            <Label>Contact phone <span className="font-normal text-gray-400">(optional)</span></Label>
            <Input
              type="tel"
              value={data.contactPhone}
              onChange={e => set('contactPhone', e.target.value)}
              placeholder="+1 555 123 4567"
            />
          </div>
          <div>
            <Label>Response time</Label>
            <select
              value={data.responseTime}
              onChange={e => set('responseTime', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="1 business day">1 business day</option>
              <option value="2 business days">2 business days</option>
              <option value="5 business days">5 business days</option>
              <option value="10 business days">10 business days</option>
            </select>
          </div>
        </div>
      )}

      {/* Step 5: Technical */}
      {step === 5 && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">Technical Details</h3>

          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-3">Technologies used on this website</legend>
            <div className="flex flex-wrap gap-2 mb-3">
              {TECH_OPTIONS.map(tech => (
                <button
                  key={tech}
                  onClick={() => toggleTech(tech)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    data.technologies.includes(tech)
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                  }`}
                  aria-pressed={data.technologies.includes(tech)}
                >
                  {tech}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Add custom technology and press Enter…"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const val = (e.target as HTMLInputElement).value.trim()
                  if (val && !data.technologies.includes(val)) {
                    set('technologies', [...data.technologies, val]);
                    (e.target as HTMLInputElement).value = ''
                  }
                }
              }}
            />
          </fieldset>

          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-3">Assessment approach</legend>
            <div className="space-y-2">
              {APPROACH_OPTIONS.map(opt => (
                <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.assessmentApproach.includes(opt.value)}
                    onChange={() => toggleApproach(opt.value)}
                    className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      )}

      {/* Step 6: Preview & Download */}
      {step === 6 && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">Your Accessibility Statement</h3>

          {/* Format toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden w-fit">
            <button
              onClick={() => setShowHtml(false)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${!showHtml ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Plain Text
            </button>
            <button
              onClick={() => setShowHtml(true)}
              className={`px-4 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${showHtml ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              HTML
            </button>
          </div>

          {/* Preview */}
          <div className="relative">
            <pre className="bg-gray-50 border border-gray-200 rounded-lg p-5 text-xs text-gray-800 overflow-auto max-h-96 whitespace-pre-wrap font-mono leading-relaxed">
              {showHtml ? generatedHtml : generatedText}
            </pre>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => copyText(showHtml ? 'html' : 'text')}
              className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors text-sm"
            >
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : `Copy ${showHtml ? 'HTML' : 'Text'}`}
            </button>
            <button
              onClick={() => downloadFile(showHtml ? 'html' : 'text')}
              className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              Download .{showHtml ? 'html' : 'txt'}
            </button>
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Start Over
            </button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
            <strong>Important:</strong> This statement is a starting point. Review it carefully and adapt it to your specific situation. Consider consulting a legal professional if your site is subject to specific national accessibility laws.
          </div>
        </div>
      )}

      {/* Navigation */}
      {step <= TOTAL_STEPS && (
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
          <button
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={() => setStep(s => Math.min(s + 1, 6))}
            disabled={!canProceed()}
            className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors text-sm disabled:opacity-40"
          >
            {step === TOTAL_STEPS ? 'Generate Statement' : 'Next'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
